import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { selectAtom } from 'jotai/utils';
import eq from 'fast-deep-equal';
import { GridGroup, GridRow, RowOrGroup } from '../types';
import { EntityKey, gridAtom } from './grid';
import { baseRowAtom, baseGroupAtom } from './plan';
import { treeStructureAtom, visibleKeysAtom, collapsedGroupsAtom } from './tree';
import { groupSummaryAtom } from './summary';

const _gridRowAtom = atomFamily((key: EntityKey) =>
  atom((get): RowOrGroup => {
    const { entities } = get(gridAtom);
    const structure = get(treeStructureAtom);
    const collapsed = get(collapsedGroupsAtom);

    const ref = entities[key];
    const meta = structure[key];
    const isExpanded = !collapsed.has(key);

    if (ref.type === 'ROW') {
      const row = get(baseRowAtom(ref.id));
      return {
        _isGroup: false,
        _key: key,
        depth: meta.depth,
        numChildren: meta.numChildren,
        isExpanded,
        ...row,
        isSelectable: true,
        groupDepth: meta.groupDepth,
        groupHeight: meta.groupHeight,
      } as GridRow;
    }

    const group = get(baseGroupAtom(ref.id));
    return {
      _isGroup: true,
      _key: key,
      depth: meta.depth,
      numChildren: meta.numChildren,
      isExpanded,
      ...group,
      summary: get(groupSummaryAtom(key)),
      isSelectable: meta.hasDescendantRows,
      groupDepth: meta.groupDepth,
      groupHeight: meta.groupHeight,
    } as GridGroup;
  }),
);

const gridRowAtom = atomFamily((key: EntityKey) => selectAtom(_gridRowAtom(key), (v) => v, eq));

export const gridRowsAtom = atom<RowOrGroup[]>((get) => {
  const keys = get(visibleKeysAtom);
  return keys.map((key) => get(gridRowAtom(key)));
});
